import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { apiClient } from "../../utils/apiClient";
import type {
  CheckShipmentConflictsRequest,
  CheckShipmentConflictsResponse,
  MergeShipmentRequest,
  MergeShipmentResponse,
} from "@services/shipments/types";
import { TinyValueComponent } from "../../internal/components/TinyValueComponent";

interface MergeShipmentActionProps {
  shipmentId: string;
  tags: string[];
  onSuccess?: () => void;
  disabled?: boolean;
}

export function MergeShipmentAction({
  shipmentId,
  tags,
  onSuccess,
  disabled = false,
}: MergeShipmentActionProps) {
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [conflictsResult, setConflictsResult] =
    useState<CheckShipmentConflictsResponse | null>(null);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState<MergeShipmentResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const handleCheckConflicts = async () => {
    try {
      setCheckingConflicts(true);
      setConflictsResult(null);
      setError(null);

      const request: CheckShipmentConflictsRequest = {
        shipmentId,
        tags,
      };

      const response = await apiClient.post<CheckShipmentConflictsResponse>(
        "/shipments/check-conflicts",
        request
      );

      setConflictsResult(response);
    } catch (err) {
      console.error("Error checking conflicts:", err);
      setError("Failed to check conflicts");
    } finally {
      setCheckingConflicts(false);
    }
  };

  const handleMerge = async () => {
    try {
      setMerging(true);
      setMergeResult(null);
      setError(null);

      const request: MergeShipmentRequest = {
        shipmentId,
        tags,
      };

      const response = await apiClient.post<MergeShipmentResponse>(
        "/shipments/merge",
        request
      );

      setMergeResult(response);

      if (response.success && response.mergedWithShipment) {
        // Notify parent component of successful merge
        onSuccess?.();
      }
    } catch (err) {
      console.error("Error merging shipment:", err);
      setError("Failed to merge shipment");
    } finally {
      setMerging(false);
    }
  };

  return (
    <Stack spacing={2} width={"100%"}>
      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          onClick={handleCheckConflicts}
          disabled={disabled || checkingConflicts || merging}
          color="info"
          variant="outlined"
          size="small"
        >
          {checkingConflicts ? (
            <CircularProgress size={20} />
          ) : (
            "Check Conflicts"
          )}
        </Button>
        <Button
          onClick={handleMerge}
          disabled={disabled || merging || checkingConflicts}
          color="warning"
          variant="outlined"
          size="small"
        >
          {merging ? <CircularProgress size={20} /> : "Merge"}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Conflicts Result */}
      {conflictsResult && (
        <Alert
          severity={
            conflictsResult.globalConflicts.length > 0 ? "warning" : "info"
          }
          onClose={() => setConflictsResult(null)}
        >
          <Typography variant="subtitle2">Conflict Check Results:</Typography>
          {conflictsResult.globalConflicts.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                <b>Found {conflictsResult.globalConflicts.length} Shipment:</b>
              </Typography>
              {conflictsResult.globalConflicts.map((conflict, index) => (
                <>
                  <Typography
                    key={index}
                    variant="body2"
                    whiteSpace={"pre-wrap"}
                  >
                    {conflict.conflictReason}
                  </Typography>
                  {conflictsResult.globalConflicts.length > 1 ? (
                    <Divider />
                  ) : null}
                </>
              ))}
            </>
          ) : (
            <Typography variant="body2">No conflicts found.</Typography>
          )}
          {conflictsResult.mergeCandidates.length > 0 && (
            <>
              <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                Can merge with {conflictsResult.mergeCandidates.length}{" "}
                shipments:
              </Typography>
              {conflictsResult.mergeCandidates.map((candidate) => (
                <Typography key={candidate.id} variant="body2">
                  • <TinyValueComponent value={candidate.id} /> (Created:{" "}
                  {new Date(candidate.createdAt).toLocaleString()})
                </Typography>
              ))}
            </>
          )}
        </Alert>
      )}

      {/* Merge Result */}
      {mergeResult && (
        <Alert
          severity={mergeResult.success ? "success" : "error"}
          onClose={() => setMergeResult(null)}
        >
          <Typography variant="subtitle2">Merge Results:</Typography>
          {mergeResult.success ? (
            <>
              {mergeResult.mergedWithShipment ? (
                <Typography variant="body2">
                  Successfully merged with shipment:{" "}
                  <TinyValueComponent
                    value={mergeResult.mergedWithShipment.id}
                  />
                </Typography>
              ) : (
                <Typography variant="body2">
                  No merge performed (no compatible shipments found).
                </Typography>
              )}

              {mergeResult.updateLog && (
                <>
                  {mergeResult.updateLog.added.length > 0 && (
                    <Typography variant="body2">
                      Added {mergeResult.updateLog.added.length} properties
                    </Typography>
                  )}
                  {mergeResult.updateLog.updated.length > 0 && (
                    <Typography variant="body2">
                      Updated {mergeResult.updateLog.updated.length} properties
                    </Typography>
                  )}
                </>
              )}

              {/* Show conflicts even on successful merge */}
              {mergeResult.globalConflicts.length > 0 && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, mb: 1, fontWeight: "bold" }}
                  >
                    Note: The following conflicts existed in the db but merge
                    proceeded with the non-conflicting shipment:
                  </Typography>
                  {mergeResult.globalConflicts.map((conflict, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      color="warning.main"
                    >
                      • {conflict.conflictReason}
                    </Typography>
                  ))}
                </>
              )}

              {mergeResult.otherMergeCandidates.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
                    Other shipments that could have been merged:
                  </Typography>
                  {mergeResult.otherMergeCandidates.map((candidate) => (
                    <Typography key={candidate.id} variant="body2">
                      • <TinyValueComponent value={candidate.id} /> (Created:{" "}
                      {new Date(candidate.createdAt).toLocaleString()})
                    </Typography>
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2">Merge failed:</Typography>
              {mergeResult.mergeErrors.map((err, index) => (
                <Typography key={index} variant="body2">
                  • {err}
                </Typography>
              ))}
            </>
          )}
        </Alert>
      )}
    </Stack>
  );
}
